// The Production Board's drag and drop.
//
// Native HTML5 drag events rather than a library: the board moves whole cards
// between eight columns, which is exactly what the browser already does.
//
// Two rules the markup can't express on its own:
//   * a card may only enter a column its status is allowed to reach, so the
//     legal targets light up on pick-up and an illegal drop is refused with a
//     reason rather than silently snapping back;
//   * the server is the authority. The card moves optimistically, and if the
//     PATCH comes back unhappy the card goes home and says why — the same
//     contract as auston_board_controller: a card sitting under a heading the
//     database never agreed to is worse than no drag and drop at all.
import { Controller } from "@hotwired/stimulus"

// Kept in step with ProductionOrder::TRANSITIONS.
const TRANSITIONS = {
  ordered: ["brief_approved"],
  brief_approved: ["in_production", "ordered"],
  in_production: ["delivered", "brief_approved"],
  delivered: ["adjustment_requested", "customer_approved", "in_production"],
  adjustment_requested: ["in_production"],
  customer_approved: ["invoiced", "delivered"],
  invoiced: ["paid", "customer_approved"],
  paid: ["invoiced"]
}

const LABELS = {
  ordered: "Ordered", brief_approved: "Brief approved", in_production: "In production",
  delivered: "Delivered", adjustment_requested: "Adjustment requested",
  customer_approved: "Approved by customer", invoiced: "Invoiced", paid: "Paid / archived"
}

// The client makes these from their own review screen. An operator dragging a
// card there would be answering on the brand's behalf, so the board says no and
// explains where the move actually lives.
//
// Kept in step with ProductionOrder::BOARD_REFUSES, which is what renders the
// card's move menu server-side. This copy exists for the drag's live
// highlighting and for rebuilding a menu after a move.
const CLIENT_MOVES = ["adjustment_requested", "customer_approved"]

export default class extends Controller {
  static targets = ["column", "card", "toast"]
  static values = { url: String }

  connect() {
    this.dragging = null
    // A click anywhere else closes an open move menu. The menu's own handlers
    // stop propagation, so this never fires for a click inside one.
    this.dismiss = () => this.closeMenus()
    document.addEventListener("click", this.dismiss)
  }

  disconnect() {
    document.removeEventListener("click", this.dismiss)
  }

  start(event) {
    const card = event.currentTarget
    this.dragging = card
    this.origin = card.parentElement
    this.originStatus = card.dataset.status
    card.classList.add("is-dragging")
    event.dataTransfer.effectAllowed = "move"
    event.dataTransfer.setData("text/plain", card.dataset.id)

    const allowed = TRANSITIONS[this.originStatus] || []
    this.columnTargets.forEach((col) => {
      if (col.dataset.status === this.originStatus) return
      col.classList.add(allowed.includes(col.dataset.status) ? "is-ok-target" : "is-no-target")
    })
  }

  end() {
    if (this.dragging) this.dragging.classList.remove("is-dragging")
    this.clearHighlights()
    this.dragging = null
  }

  over(event) {
    if (!this.dragging) return
    event.preventDefault()
    const column = event.currentTarget
    if (column.dataset.status === this.originStatus) return
    column.classList.add(this.allowed(column) ? "is-over" : "is-no")
  }

  leave(event) {
    event.currentTarget.classList.remove("is-over", "is-no")
  }

  drop(event) {
    event.preventDefault()
    const column = event.currentTarget
    column.classList.remove("is-over", "is-no")
    const card = this.dragging
    if (!card) return

    const target = column.dataset.status
    if (target === this.originStatus) return

    if (!this.allowed(column)) {
      this.toast(`Can't go from “${LABELS[this.originStatus]}” to “${LABELS[target]}”`)
      return
    }
    if (CLIENT_MOVES.includes(target)) {
      this.toast(`“${LABELS[target]}” is the client's move — it happens on their review screen`)
      return
    }

    this.commit(card, target, column)
  }

  // ---- the card's own move menu -----------------------------------------
  //
  // Same PATCH, same optimism, same revert. A drag is a poor instrument across
  // eight columns on a trackpad and it does not exist at all on a touch screen,
  // so the card also says where it may go and takes a click for it.

  menu(event) {
    event.stopPropagation()
    const wrap = event.currentTarget.closest(".pb-move")
    const list = wrap.querySelector(".pb-move__list")
    const wasOpen = !list.hidden

    this.closeMenus()
    if (wasOpen) return

    list.hidden = false
    wrap.classList.add("is-open")
    // Cards are siblings in a flex column, so a later one paints over an open
    // menu. Raising the card that owns it is what keeps the list on top.
    wrap.closest(".pb-card")?.classList.add("is-menu")
    event.currentTarget.setAttribute("aria-expanded", "true")
  }

  pick(event) {
    event.stopPropagation()
    const button = event.currentTarget
    const card = button.closest(".pb-card")
    const target = button.dataset.targetStatus

    this.closeMenus()
    if (!card || !target || target === card.dataset.status) return

    this.commit(card, target)
  }

  closeMenus() {
    this.element.querySelectorAll(".pb-move__list").forEach((list) => (list.hidden = true))
    this.element.querySelectorAll(".pb-move").forEach((wrap) => {
      wrap.classList.remove("is-open")
      wrap.closest(".pb-card")?.classList.remove("is-menu")
      const button = wrap.querySelector(".pb-move__btn")
      if (button) button.setAttribute("aria-expanded", "false")
    })
  }

  // ---- the one write ------------------------------------------------------

  // Optimistic: move it now, put it back if the server disagrees. Origin is
  // captured here rather than read off the controller, so a menu pick — which
  // never went through a drag — reverts to the right column too.
  commit(card, target, column = null) {
    const destination = column || this.columnTargets.find((col) => col.dataset.status === target)
    if (!destination) return

    const origin = card.parentElement
    const originStatus = card.dataset.status

    destination.appendChild(card)
    card.dataset.status = target
    this.rebuildMenu(card, target)
    this.recount()

    this.save(card, target, origin, originStatus)
  }

  // The card just changed status, so the moves it offers changed with it.
  rebuildMenu(card, status) {
    const wrap = card.querySelector(".pb-move")
    if (!wrap) return

    const targets = (TRANSITIONS[status] || []).filter((t) => !CLIENT_MOVES.includes(t))
    if (targets.length === 0) {
      wrap.hidden = true
      return
    }

    wrap.hidden = false
    wrap.querySelector(".pb-move__list").innerHTML = targets
      .map(
        (t) =>
          `<button type="button" class="pb-move__opt" draggable="false" data-target-status="${t}" ` +
          `data-action="click->production-board#pick">` +
          `<i style="background: ${this.colorFor(t)};"></i>${LABELS[t]}</button>`
      )
      .join("")
  }

  // Read the column's own dot rather than keeping a second colour table here.
  colorFor(status) {
    const column = this.columnTargets.find((col) => col.dataset.status === status)
    const dot = column && column.parentElement.querySelector(".pb-col__dot")
    return dot ? dot.style.background : "transparent"
  }

  // Clicking a card opens the order — but not when the click is the tail end of
  // a drag, which is why it checks for the dragging class first.
  open(event) {
    const card = event.currentTarget
    if (card.classList.contains("is-dragging")) return
    window.location = card.dataset.url
  }

  async save(card, target, origin, originStatus) {
    const url = this.urlValue.replace(/\/0\/move$/, `/${card.dataset.id}/move`)
    try {
      const response = await fetch(url, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          "X-CSRF-Token": document.querySelector("meta[name='csrf-token']")?.content
        },
        body: JSON.stringify({ status: target })
      })
      const data = await response.json()

      if (!response.ok || !data.ok) {
        this.revert(card, origin, originStatus, data.error || "That move was refused.")
        return
      }
      this.toast(`Moved to ${data.label}`)
    } catch (error) {
      this.revert(card, origin, originStatus, "Couldn't reach the server — the card is back where it was.")
    }
  }

  revert(card, origin, originStatus, message) {
    origin.appendChild(card)
    card.dataset.status = originStatus
    this.rebuildMenu(card, originStatus)
    this.recount()
    this.toast(message)
  }

  allowed(column) {
    return (TRANSITIONS[this.originStatus] || []).includes(column.dataset.status)
  }

  clearHighlights() {
    this.columnTargets.forEach((col) =>
      col.classList.remove("is-over", "is-no", "is-ok-target", "is-no-target")
    )
  }

  recount() {
    this.columnTargets.forEach((col) => {
      const counter = col.parentElement.querySelector(".pb-col__c")
      if (counter) counter.textContent = col.querySelectorAll(".pb-card").length
    })
  }

  toast(message) {
    if (!this.hasToastTarget) return
    const el = this.toastTarget
    el.textContent = message
    el.classList.add("is-show")
    clearTimeout(this.toastTimer)
    this.toastTimer = setTimeout(() => el.classList.remove("is-show"), 2600)
  }
}
